import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { usePackageOfferings } from "@/providers/ContentProvider";
import { useToast } from "@/hooks/use-toast";
import {
  IntakeMode,
  IntakeFormData,
  IntakeLightFormData,
  initialProjectBasicsData,
  initialVerificationContactData,
  initialConfidentialityData,
  initialQuickBasicsData,
  initialLightVerificationContactData,
  isRecurringPackage,
  isSensitivePackage,
  OPTIMIZER_TIER_OPTIONS,
} from "@/domain/intake";
import { ProjectBasicsStep } from "./ProjectBasicsStep";
import { ConfidentialityStep } from "./ConfidentialityStep";
import { ReviewStep } from "./ReviewStep";
import { QuickBasicsStep } from "./QuickBasicsStep";
import { ContactSubmitStep } from "./ContactSubmitStep";
import { onboardingService } from "@/services";

const FULL_STEPS = [
  { id: "project-basics", label: "Project Basics" },
  { id: "confidentiality", label: "Confidentiality" },
  { id: "review", label: "Review" },
] as const;

const LIGHT_STEPS = [
  { id: "quick-basics", label: "About You" },
  { id: "contact-submit", label: "Contact & Review" },
] as const;

type FullStepId = (typeof FULL_STEPS)[number]["id"];
type LightStepId = (typeof LIGHT_STEPS)[number]["id"];

const LOCAL_STORAGE_KEY = "echopeak-intake-v3";
const LIGHT_LOCAL_STORAGE_KEY = "echopeak-intake-light-v3";
const HONEYPOT_FIELD_NAME = "website_url";
const MIN_SUBMISSION_TIME_MS = 5000;

function loadSavedData<T>(key: string): T | null {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

function saveData<T>(key: string, data: T) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {}
}

function resolveMode(searchParams: URLSearchParams): IntakeMode {
  if (searchParams.get("packageId")) return "package";
  if (searchParams.get("mode") === "call") return "call";
  return "call";
}

export default function IntakePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getOfferingById } = usePackageOfferings();
  const formStartedAt = useRef(Date.now());
  const honeypotRef = useRef("");

  const mode = resolveMode(searchParams);
  const packageIdFromUrl = searchParams.get("packageId");

  if (mode === "package") {
    return (
      <FullIntake
        packageIdFromUrl={packageIdFromUrl}
        navigate={navigate}
        toast={toast}
        getOfferingById={getOfferingById}
        formStartedAt={formStartedAt}
        honeypotRef={honeypotRef}
      />
    );
  }

  return (
    <LightIntake
      navigate={navigate}
      toast={toast}
      formStartedAt={formStartedAt}
      honeypotRef={honeypotRef}
    />
  );
}

function HoneypotField({
  honeypotRef,
}: {
  honeypotRef: React.MutableRefObject<string>;
}) {
  return (
    <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
      <label htmlFor={HONEYPOT_FIELD_NAME}>Website</label>
      <input
        type="text"
        id={HONEYPOT_FIELD_NAME}
        name={HONEYPOT_FIELD_NAME}
        tabIndex={-1}
        autoComplete="off"
        value={honeypotRef.current}
        onChange={(e) => {
          honeypotRef.current = e.target.value;
        }}
      />
    </div>
  );
}

function isSpamSubmission(
  honeypotValue: string,
  formStartedAt: number
): boolean {
  if (honeypotValue.trim() !== "") return true;
  if (Date.now() - formStartedAt < MIN_SUBMISSION_TIME_MS) return true;
  return false;
}

interface StepIndicatorProps {
  steps: ReadonlyArray<{ id: string; label: string }>;
  currentStepIndex: number;
  currentStepId: string;
  onStepClick: (id: string) => void;
}

function StepIndicator({
  steps,
  currentStepIndex,
  currentStepId,
  onStepClick,
}: StepIndicatorProps) {
  return (
    <div className="flex justify-center mb-8 md:mb-12">
      <div className="flex items-center gap-2 md:gap-4">
        {steps.map((step, index) => {
          const isCompleted = index < currentStepIndex;
          const isCurrent = step.id === currentStepId;
          return (
            <div key={step.id} className="flex items-center gap-2 md:gap-4">
              {index > 0 && (
                <div
                  className={`h-px w-8 md:w-16 transition-colors ${
                    isCompleted ? "bg-primary" : "bg-border"
                  }`}
                />
              )}
              <button
                onClick={() => {
                  if (isCompleted) onStepClick(step.id);
                }}
                disabled={!isCompleted && !isCurrent}
                className={`flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 rounded-lg transition-all ${
                  isCurrent
                    ? "bg-primary text-primary-foreground"
                    : isCompleted
                      ? "bg-primary/20 text-primary cursor-pointer hover:bg-primary/30"
                      : "bg-muted text-muted-foreground"
                }`}
              >
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold ${
                    isCompleted
                      ? "bg-primary text-primary-foreground"
                      : isCurrent
                        ? "bg-primary-foreground text-primary"
                        : "bg-muted-foreground/30"
                  }`}
                >
                  {isCompleted ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    index + 1
                  )}
                </span>
                <span className="hidden md:inline font-medium">
                  {step.label}
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LightIntake({
  navigate,
  toast,
  formStartedAt,
  honeypotRef,
}: {
  navigate: ReturnType<typeof useNavigate>;
  toast: ReturnType<typeof useToast>["toast"];
  formStartedAt: React.MutableRefObject<number>;
  honeypotRef: React.MutableRefObject<string>;
}) {
  const [currentStep, setCurrentStep] = useState<LightStepId>("quick-basics");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<IntakeLightFormData>(() => {
    const saved = loadSavedData<IntakeLightFormData>(LIGHT_LOCAL_STORAGE_KEY);
    if (saved) {
      const qb = saved.quickBasics;
      return {
        ...saved,
        quickBasics: {
          ...qb,
          timezone: qb.timezone ?? saved.verificationContact?.timezone ?? initialQuickBasicsData.timezone,
        },
      };
    }
    return {
      quickBasics: initialQuickBasicsData,
      verificationContact: initialLightVerificationContactData,
    };
  });

  useEffect(() => {
    saveData(LIGHT_LOCAL_STORAGE_KEY, formData);
  }, [formData]);

  const currentStepIndex = LIGHT_STEPS.findIndex((s) => s.id === currentStep);

  const handleNext = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < LIGHT_STEPS.length) {
      setCurrentStep(LIGHT_STEPS[nextIndex].id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStepIndex]);

  const handleBack = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(LIGHT_STEPS[prevIndex].id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStepIndex]);

  const handleSubmit = async () => {
    if (isSpamSubmission(honeypotRef.current, formStartedAt.current)) {
      toast({
        title: "Request submitted",
        description: "We'll be in touch shortly.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onboardingService.createLead({
        type: "custom",
        formData: {
          companyName: formData.quickBasics.name,
          companyIndustry: "technology",
          projectDescription: formData.quickBasics.discussionTopic,
          deliverables: "",
          acceptanceCriteria: "",
          turnstileToken: "",
        },
        intakeMetadata: {
          email: formData.quickBasics.email,
          name: formData.quickBasics.name,
          ndaRequired: false,
          summary: formData.quickBasics.discussionTopic,
          packageId: null,
          mode: "call",
          timezone: formData.quickBasics.timezone,
          meetingLength: formData.verificationContact.preferredMeetingLength,
        },
      });

      localStorage.setItem(
        "echopeak-lead-context",
        JSON.stringify({
          leadId: result.leadId,
          mode: "call" as IntakeMode,
          email: formData.quickBasics.email,
          name: formData.quickBasics.name,
          packageId: null,
          timezone: formData.quickBasics.timezone,
          meetingLength: formData.verificationContact.preferredMeetingLength,
          ndaRequired: false,
          summary: formData.quickBasics.discussionTopic,
        })
      );

      localStorage.removeItem(LIGHT_LOCAL_STORAGE_KEY);
      navigate("/verify/email");
    } catch {
      toast({
        title: "Submission failed",
        description: "Unable to submit your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-8 md:py-16">
      <div className="absolute inset-0 hero-gradient" />
      <div className="absolute inset-0 grid-background opacity-40" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        <StepIndicator
          steps={LIGHT_STEPS}
          currentStepIndex={currentStepIndex}
          currentStepId={currentStep}
          onStepClick={(id) => setCurrentStep(id as LightStepId)}
        />

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <HoneypotField honeypotRef={honeypotRef} />

            <AnimatePresence mode="wait">
              {currentStep === "quick-basics" && (
                <motion.div
                  key="quick-basics"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <QuickBasicsStep
                    data={formData.quickBasics}
                    onChange={(data) =>
                      setFormData((prev) => ({
                        ...prev,
                        quickBasics: data,
                      }))
                    }
                    onNext={handleNext}
                  />
                </motion.div>
              )}

              {currentStep === "contact-submit" && (
                <motion.div
                  key="contact-submit"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <ContactSubmitStep
                    contactData={formData.verificationContact}
                    quickBasics={formData.quickBasics}
                    onContactChange={(data) =>
                      setFormData((prev) => ({
                        ...prev,
                        verificationContact: data,
                      }))
                    }
                    onSubmit={handleSubmit}
                    onBack={handleBack}
                    isSubmitting={isSubmitting}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-8">
              <Card className="glass-card border-gradient">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4">Summary</h3>
                  <div className="space-y-4">
                    <div className="p-3 bg-primary/10 rounded-lg border border-primary/30">
                      <p className="text-xs text-muted-foreground mb-1">
                        Call Type
                      </p>
                      <p className="font-semibold">Exploratory Discovery Call</p>
                      <p className="text-sm text-muted-foreground">
                        Discuss your needs and explore the right package
                      </p>
                    </div>

                    {formData.quickBasics.companyName && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Company
                        </p>
                        <p className="font-medium">
                          {formData.quickBasics.companyName}
                        </p>
                      </div>
                    )}

                    {formData.quickBasics.email && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Email
                        </p>
                        <p className="font-medium">
                          {formData.quickBasics.email}
                        </p>
                      </div>
                    )}

                    {formData.quickBasics.timezone && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Time Zone
                        </p>
                        <p className="font-medium">
                          {formData.quickBasics.timezone}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FullIntake({
  packageIdFromUrl,
  navigate,
  toast,
  getOfferingById,
  formStartedAt,
  honeypotRef,
}: {
  packageIdFromUrl: string | null;
  navigate: ReturnType<typeof useNavigate>;
  toast: ReturnType<typeof useToast>["toast"];
  getOfferingById: ReturnType<typeof usePackageOfferings>["getOfferingById"];
  formStartedAt: React.MutableRefObject<number>;
  honeypotRef: React.MutableRefObject<string>;
}) {
  const [currentStep, setCurrentStep] = useState<FullStepId>("project-basics");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<IntakeFormData>(() => {
    const saved = loadSavedData<IntakeFormData>(LOCAL_STORAGE_KEY);
    if (saved) {
      const pb = saved.projectBasics;
      return {
        ...saved,
        projectBasics: {
          ...pb,
          timezone: pb.timezone ?? saved.verificationContact?.timezone ?? initialProjectBasicsData.timezone,
          preferredMeetingLength: pb.preferredMeetingLength ?? saved.verificationContact?.preferredMeetingLength ?? initialProjectBasicsData.preferredMeetingLength,
        },
      };
    }
    return {
      projectBasics: {
        ...initialProjectBasicsData,
        selectedPackageId: packageIdFromUrl || null,
      },
      verificationContact: initialVerificationContactData,
      confidentiality: initialConfidentialityData,
    };
  });

  useEffect(() => {
    if (
      packageIdFromUrl &&
      formData.projectBasics.selectedPackageId !== packageIdFromUrl
    ) {
      setFormData((prev) => ({
        ...prev,
        projectBasics: {
          ...prev.projectBasics,
          selectedPackageId: packageIdFromUrl,
        },
      }));
    }
  }, [packageIdFromUrl]);

  useEffect(() => {
    saveData(LOCAL_STORAGE_KEY, formData);
  }, [formData]);

  const currentStepIndex = FULL_STEPS.findIndex((s) => s.id === currentStep);

  const handleNext = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < FULL_STEPS.length) {
      setCurrentStep(FULL_STEPS[nextIndex].id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStepIndex]);

  const handleBack = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(FULL_STEPS[prevIndex].id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStepIndex]);

  const handleSubmit = async () => {
    if (isSpamSubmission(honeypotRef.current, formStartedAt.current)) {
      toast({
        title: "Request submitted",
        description: "We'll be in touch shortly.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const ndaRequired =
        formData.confidentiality.ndaRequested ||
        (isSensitivePackage(formData.projectBasics.selectedPackageId) &&
          formData.confidentiality.willShareSensitiveData);

      const result = await onboardingService.createLead({
        type: "package",
        formData: {
          packageId: formData.projectBasics.selectedPackageId ?? "",
          company: formData.projectBasics.companyName,
          companyIndustry: formData.projectBasics.industryId,
          expectations: [],
          milestones: [],
          features: [],
          budget: "",
          deadlineDate: "",
          otherCriteria:
            formData.projectBasics.projectSummary ||
            formData.projectBasics.currentSituation ||
            "",
          attachments: [],
          turnstileToken: "",
        },
        intakeMetadata: {
          email: formData.projectBasics.email,
          name: formData.projectBasics.name,
          ndaRequired,
          summary:
            formData.projectBasics.projectSummary ||
            formData.projectBasics.currentSituation ||
            "",
          packageId: formData.projectBasics.selectedPackageId,
          mode: "package",
          timezone: formData.projectBasics.timezone,
          meetingLength: formData.projectBasics.preferredMeetingLength,
        },
      });

      localStorage.setItem(
        "echopeak-lead-context",
        JSON.stringify({
          leadId: result.leadId,
          mode: "package" as IntakeMode,
          email: formData.projectBasics.email,
          name: formData.projectBasics.name,
          packageId: formData.projectBasics.selectedPackageId,
          timezone: formData.projectBasics.timezone,
          meetingLength: formData.projectBasics.preferredMeetingLength,
          ndaRequired,
          summary:
            formData.projectBasics.projectSummary ||
            formData.projectBasics.currentSituation ||
            "",
        })
      );

      localStorage.removeItem(LOCAL_STORAGE_KEY);
      navigate("/verify/email");
    } catch {
      toast({
        title: "Submission failed",
        description: "Unable to submit your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedPackage = formData.projectBasics.selectedPackageId
    ? getOfferingById(formData.projectBasics.selectedPackageId) ?? null
    : null;

  return (
    <div className="min-h-screen py-8 md:py-16">
      <div className="absolute inset-0 hero-gradient" />
      <div className="absolute inset-0 grid-background opacity-40" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        <StepIndicator
          steps={FULL_STEPS}
          currentStepIndex={currentStepIndex}
          currentStepId={currentStep}
          onStepClick={(id) => setCurrentStep(id as FullStepId)}
        />

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <HoneypotField honeypotRef={honeypotRef} />

            <AnimatePresence mode="wait">
              {currentStep === "project-basics" && (
                <motion.div
                  key="project-basics"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <ProjectBasicsStep
                    data={formData.projectBasics}
                    onChange={(data) =>
                      setFormData((prev) => ({
                        ...prev,
                        projectBasics: data,
                      }))
                    }
                    onNext={handleNext}
                    preselectedPackageId={packageIdFromUrl || undefined}
                  />
                </motion.div>
              )}

              {currentStep === "confidentiality" && (
                <motion.div
                  key="confidentiality"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <ConfidentialityStep
                    data={formData.confidentiality}
                    onChange={(data) =>
                      setFormData((prev) => ({
                        ...prev,
                        confidentiality: data,
                      }))
                    }
                    onNext={handleNext}
                    onBack={handleBack}
                    selectedPackageId={
                      formData.projectBasics.selectedPackageId
                    }
                  />
                </motion.div>
              )}

              {currentStep === "review" && (
                <motion.div
                  key="review"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <ReviewStep
                    formData={formData}
                    selectedPackage={selectedPackage}
                    onSubmit={handleSubmit}
                    onBack={handleBack}
                    isSubmitting={isSubmitting}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-8">
              <Card className="glass-card border-gradient">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4">Summary</h3>
                  <div className="space-y-4">
                    {selectedPackage && (() => {
                      const isRecurring = isRecurringPackage(formData.projectBasics.selectedPackageId);
                      const selectedTier = formData.projectBasics.tierIntent
                        ? OPTIMIZER_TIER_OPTIONS.find((t) => t.id === formData.projectBasics.tierIntent)
                        : null;
                      const matchedRetainerTier = selectedTier && selectedPackage.pricing.retainerTiers
                        ? selectedPackage.pricing.retainerTiers.find(
                            (rt) => rt.name.toLowerCase().replace(/^the\s+/, "") === selectedTier.id
                          )
                        : null;

                      return (
                        <div className="p-3 bg-primary/10 rounded-lg border border-primary/30">
                          <p className="text-xs text-muted-foreground mb-1">
                            Selected Package
                          </p>
                          <p className="font-semibold">{selectedPackage.name}</p>
                          {isRecurring && selectedTier ? (
                            <>
                              <p className="text-sm text-primary font-semibold mt-1">
                                {selectedTier.name} — {matchedRetainerTier
                                  ? `$${matchedRetainerTier.monthlyPrice.toLocaleString()}/mo`
                                  : selectedTier.price}
                              </p>
                              {matchedRetainerTier && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {matchedRetainerTier.responseTime} response
                                </p>
                              )}
                            </>
                          ) : selectedPackage.pricing.priceRange ? (
                            <p className="text-sm text-muted-foreground">
                              ${selectedPackage.pricing.priceRange.min.toLocaleString()}
                              {isRecurring ? "/mo" : ""} – $
                              {selectedPackage.pricing.priceRange.max.toLocaleString()}
                              {isRecurring ? "/mo" : ""}
                            </p>
                          ) : null}
                        </div>
                      );
                    })()}

                    {formData.projectBasics.companyName && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Company
                        </p>
                        <p className="font-medium">
                          {formData.projectBasics.companyName}
                        </p>
                      </div>
                    )}

                    {formData.projectBasics.email && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Email
                        </p>
                        <p className="font-medium">
                          {formData.projectBasics.email}
                        </p>
                      </div>
                    )}

                    {formData.projectBasics.timezone && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Time Zone
                        </p>
                        <p className="font-medium">
                          {formData.projectBasics.timezone}
                        </p>
                      </div>
                    )}

                    {(formData.confidentiality.ndaRequested ||
                      formData.confidentiality.willShareSensitiveData) && (
                      <div className="p-3 bg-primary/10 rounded-lg border border-primary/30">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            NDA Required
                          </Badge>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
